import {boardCellKey, type CellAddress} from '@github-ui/board-core'

import type {SavedViewsBoardCellPage, SavedViewsBoardItem, SavedViewsBoardPayload} from './types'

/**
 * A board holding one cell's next page. Every other cell keeps its identity, so appending
 * re-renders that cell rather than the grid, and counts are carried through untouched: a
 * `totalCount` was settled by the request that drew the board (github/views#20).
 *
 * A duplicate card means the cell moved underneath the cursor rather than that the page was
 * wrong, so the repeat is dropped instead of drawn twice.
 */
export function appendCellPage<TItem extends SavedViewsBoardItem, TMetadata>(
  payload: SavedViewsBoardPayload<TItem, TMetadata>,
  address: CellAddress,
  page: SavedViewsBoardCellPage<TItem>,
): SavedViewsBoardPayload<TItem, TMetadata> {
  const key = boardCellKey(address)
  // A page naming a different cell belongs to neither: not the cell it names, whose cursor it
  // would overwrite, nor the cell that asked. The adapter refuses one first, so this is the
  // backstop for a domain implementing `fetchCellPage` without that check.
  if (boardCellKey(page) !== key) return payload
  const {data, cellCursors} = payload.boardData

  let appended = false
  const cells = data.cells.map(cell => {
    if (boardCellKey(cell) !== key) return cell

    appended = true
    const held = new Set(cell.items.map(item => item.globalId))
    const arriving = page.items.filter(item => !held.has(item.globalId))

    return {...cell, items: [...cell.items, ...arriving], itemsLoaded: true, hasMoreItems: page.hasMoreItems}
  })

  // The grid moved on while the page was in flight, so there is nothing to append to.
  if (!appended) return payload

  const cursors = new Map(cellCursors)
  if (page.itemsCursor == null) cursors.delete(key)
  else cursors.set(key, page.itemsCursor)

  return {
    ...payload,
    boardData: {...payload.boardData, data: {...data, cells}, cellCursors: cursors},
  }
}
